/*
 * Embed codes for the Interactive Avatar Panel V2.
 *
 * The recommended install is a single <script> tag pointing at the static
 * avatar-panel-v2.js loader, exactly like the Combo Widget V2 snippet. Any
 * setting saved in the customizer travels with the agent, so the snippet stays
 * one line; the optional overrides below are only for per-page differences.
 */
import React, { useState } from 'react';
import {
  X, Copy, CheckCircle, Code, Link, Palette, Sparkles, PanelRight, Maximize2, AlertTriangle,
  MousePointerClick,
} from 'lucide-react';
import Toast from './Toast';
import AvatarPanelThemeCustomizer from './AvatarPanelThemeCustomizer';
import { avatarPanelParams } from './AvatarPanelV2';

const COPY_LABELS = {
  drawer: 'Side drawer snippet',
  spotlight: 'Spotlight snippet',
  inline: 'Inline iframe',
  url: 'Direct URL',
  api: 'Open-from-page example',
  cobrowse: 'Co-browse snippet',
};

const CopyButton = ({ text, type, copied, onCopy }) => (
  <button
    type="button"
    onClick={() => onCopy(text, type)}
    className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-1.5 text-sm flex-shrink-0 self-start"
  >
    {copied[type] ? (
      <><CheckCircle className="w-3.5 h-3.5" />Copied</>
    ) : (
      <><Copy className="w-3.5 h-3.5" />Copy</>
    )}
  </button>
);

const CodeBlock = ({ value }) => (
  <div className="flex-1 min-w-0 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground overflow-x-auto max-h-44 overflow-y-auto">
    <pre className="whitespace-pre-wrap break-words">{value}</pre>
  </div>
);

const Section = ({ icon: Icon, title, badge, children }) => (
  <div className="space-y-2.5">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-primary" />
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {badge && (
        <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
          {badge}
        </span>
      )}
    </div>
    {children}
  </div>
);

const AvatarPanelEmbedModal = ({ agent, onClose }) => {
  const [copied, setCopied] = useState({});
  const [toast, setToast] = useState(null);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [agentState, setAgentState] = useState(agent);

  const baseUrl = window.location.origin;
  const loaderSrc = `${baseUrl}/avatar-panel-v2.js?agent=${agentState.id}`;
  const panelUrl = `${baseUrl}/embed/avatar-panel-v2/${agentState.id}`;

  const avatarReady =
    !!agentState.liveavatar_enabled &&
    !!agentState.liveavatar_avatar_id &&
    String(agentState.liveavatar_avatar_id).trim() !== '';

  const savedVariant = agentState.avatar_panel_config?.variant || 'drawer';

  const drawerSnippet = `<script src="${loaderSrc}" async></script>`;
  const spotlightSnippet = `<script src="${loaderSrc}&variant=spotlight&position=bottom-center" async></script>`;
  const cobrowseSnippet = `<script src="${loaderSrc}&cobrowse=1" async></script>`;
  const cobrowseOn = !!agentState.cobrowse_enabled && agentState.agent_type !== 'flow';
  const inlineCode =
    `<iframe\n  src="${panelUrl}?view=panel"\n  width="100%"\n  height="640"\n  frameborder="0"\n  allow="microphone; autoplay"\n  style="border-radius:20px; border:0;"\n></iframe>`;
  const apiExample = `<!-- Open the panel from your own button -->
<button data-braincx-avatar-panel>Talk to us</button>

<script>
  // Or drive it from code. Safe to call before the panel has finished loading:
  // the command is queued and replayed.
  window.BrainCXAvatarPanel.open();      // also .minimize(), .close(), .toggle()

  // Pops the live video out into a draggable card. Needs a call in progress.
  window.BrainCXAvatarPanel.pip();

  window.addEventListener('braincx-avatar-panel-ready', function () {
    console.log('panel ready');
  });
</script>`;

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied((prev) => ({ ...prev, [type]: true }));
      setToast({ message: `${COPY_LABELS[type]} copied.`, type: 'success' });
      setTimeout(() => setCopied((prev) => ({ ...prev, [type]: false })), 2000);
    } catch {
      setToast({ message: 'Failed to copy', type: 'error' });
    }
  };

  const openDemo = (variant) => {
    const params = avatarPanelParams({ ...(agentState.avatar_panel_config || {}), variant });
    params.set('agent', agentState.id);
    if (variant === 'spotlight') params.set('position', 'bottom-center');
    window.open(`${baseUrl}/avatar-panel-v2-demo.html?${params}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                Interactive Avatar Panel: {agentState.name}
              </h2>
              <p className="text-white/70 text-xs mt-0.5">
                Live avatar, transcript and typing in one session. Never dims the page behind it.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-7">
          {!avatarReady && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-semibold text-amber-500">LiveAvatar is not fully configured.</span>{' '}
                The panel needs LiveAvatar enabled and an Avatar ID on this agent, saved, before it can
                connect. The snippets below are still correct and will work once that is set.
              </div>
            </div>
          )}

          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-sm">
            <p className="font-semibold text-primary mb-1">Two patterns, one snippet</p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              The Panel is currently set to <strong>{savedVariant === 'spotlight' ? 'spotlight' : 'side drawer'}</strong> in
              the customizer, so the one-line snippet below gives you that. Adding{' '}
              <code className="font-mono bg-muted px-1 rounded">&amp;variant=…</code> to the script URL overrides it for a
              single page. Minimizing the panel keeps the conversation running in a small pill, and reopening
              returns the visitor to the same transcript.
            </p>
          </div>

          <Section icon={PanelRight} title="Website snippet" badge="Recommended">
            <div className="flex gap-2">
              <CodeBlock value={drawerSnippet} />
              <CopyButton text={drawerSnippet} type="drawer" copied={copied} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Paste once before <code className="font-mono bg-muted px-1 rounded">&lt;/body&gt;</code>. A tiny loader mounts
              a launcher-sized iframe and resizes it to the widget's real footprint, so while it is closed it
              never covers the page. Every colour, label and placement comes from the customizer.
            </p>
          </Section>

          <Section icon={Maximize2} title="Spotlight variant (per page)">
            <div className="flex gap-2">
              <CodeBlock value={spotlightSnippet} />
              <CopyButton text={spotlightSnippet} type="spotlight" copied={copied} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              A command pill at the bottom of the page that grows into a centered panel. Above 660px wide it
              splits into avatar and transcript columns. On phones it becomes a bottom sheet that leaves the
              top of the page visible and scrollable.
            </p>
          </Section>

          <Section icon={MousePointerClick} title="With co-browse (guide the page)" badge={cobrowseOn ? 'On for this agent' : undefined}>
            <div className="flex gap-2">
              <CodeBlock value={cobrowseSnippet} />
              <CopyButton text={cobrowseSnippet} type="cobrowse" copied={copied} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              The same panel, and during the conversation the avatar can scroll, open pages on your site and fill
              in forms while the visitor watches. The conversation carries on across page loads. Put it on every
              page. {cobrowseOn
                ? 'Allowed websites and safeguards are set in the Co-browse Widget settings for this agent.'
                : 'Turn on Co-browse for this agent first (agent menu > Co-browse Widget). Until then this snippet runs a normal panel.'}
            </p>
          </Section>

          <Section icon={Code} title="Open it from your own button">
            <div className="flex gap-2">
              <CodeBlock value={apiExample} />
              <CopyButton text={apiExample} type="api" copied={copied} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              The panel lives inside its own iframe, so a host page can never reach the launcher with
              <code className="font-mono bg-muted px-1 rounded">document.querySelector</code>. Add{' '}
              <code className="font-mono bg-muted px-1 rounded">data-braincx-avatar-panel</code> to any element, or call
              the API.
            </p>
          </Section>

          <Section icon={Code} title="Inline in a page section">
            <div className="flex gap-2">
              <CodeBlock value={inlineCode} />
              <CopyButton text={inlineCode} type="inline" copied={copied} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              No launcher, no floating anything: the panel is a block in the layout. Use this where one page
              carries the decision, in the slot a contact form would normally take.
            </p>
          </Section>

          <Section icon={Link} title="Direct URL">
            <div className="flex gap-2">
              <div className="flex-1 min-w-0 p-3 bg-muted/50 border border-border rounded-lg font-mono text-sm break-all">
                {panelUrl}
              </div>
              <CopyButton text={panelUrl} type="url" copied={copied} onCopy={handleCopy} />
            </div>
          </Section>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Try it on a demo page</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { variant: 'drawer', label: 'Side drawer', desc: 'Full-height panel at the edge' },
                { variant: 'spotlight', label: 'Spotlight', desc: 'Pill grows into a centered card' },
              ].map(({ variant, label, desc }) => (
                <button
                  key={variant}
                  type="button"
                  onClick={() => openDemo(variant)}
                  className="flex flex-col items-start gap-1 p-3 bg-muted/30 hover:bg-muted/60 border border-border rounded-xl text-left transition-colors"
                >
                  <span className="text-sm font-semibold text-foreground">{label}</span>
                  <span className="text-xs text-muted-foreground">{desc}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => window.open(`${panelUrl}?view=panel`, '_blank')}
                className="flex flex-col items-start gap-1 p-3 bg-muted/30 hover:bg-muted/60 border border-border rounded-xl text-left transition-colors"
              >
                <span className="text-sm font-semibold text-foreground">Panel only</span>
                <span className="text-xs text-muted-foreground">The bare embed, no launcher</span>
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              The demo page is a normal article with scrollable content, so you can check that the page stays
              readable and clickable while the conversation runs.
            </p>
          </div>
        </div>

        <div className="border-t border-border p-4 bg-muted/30 flex justify-between items-center flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowCustomizer(true)}
            className="px-4 py-2 bg-gradient-button hover:opacity-90 text-primary-foreground rounded-lg flex items-center gap-2 text-sm"
          >
            <Palette className="w-4 h-4" />
            Customize panel
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm">
            Close
          </button>
        </div>
      </div>

      {showCustomizer && (
        <AvatarPanelThemeCustomizer
          agent={agentState}
          onClose={() => setShowCustomizer(false)}
          onSave={(saved) => {
            setAgentState((prev) => ({ ...prev, ...saved }));
            setShowCustomizer(false);
          }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default AvatarPanelEmbedModal;
